// A base class that we can drive from to create services for modules. ex. tournaments, tours, etc.
import { AxiosInstance } from 'axios';
import { IResult } from './Result';
import { APIResponseHandler } from 'utilities/ErrorHelper';

export interface IBaseService<T> {
  getAll({ page, count, filters }: { page: number; count: number; filters?: string }): Promise<any>;
  getOne(id: string): Promise<T | undefined>;
  addOne(single: T): Promise<IResult<T>>;
  updateOne(single: T): Promise<IResult<T>>;
  patchOne(single: T): Promise<IResult<T>>;
  deleteOne(id: string): Promise<IResult<boolean | undefined>>;
}

export class BaseService<T> implements IBaseService<T> {
  protected _axiosInstance: AxiosInstance;
  protected _baseEndpoint: string;

  constructor(axiosInstance: AxiosInstance, baseEndpoint: string) {
    this._axiosInstance = axiosInstance;
    this._baseEndpoint = baseEndpoint;
  }

  getAll = async ({ page, count, filters }: { page: number; count: number; filters?: string }): Promise<any> => {
    const url = `${this._baseEndpoint}?pageNumber=${page}&pageSize=${count}${filters ? `${filters}` : ''}`;
    const response = await this._axiosInstance.get<IResult<T[]>>(url);

    if (response.status !== 200) throw Error(`There was an internal error while fetching data from ${url}.`);

    if (!(typeof response.data !== 'undefined' && !response.data.isError)) return;

    if (response.data.data) return { data: response.data.data };

    return { data: [], headers: {}, request: {} };
  };

  getOne = async (id: string): Promise<T | undefined> => {
    const response = await this._axiosInstance.get<IResult<T>>(`${this._baseEndpoint}/${id}`);

    if (response.status === 404) return undefined;

    if (response.data && !response.data.isError && response.data.data) return response.data.data;

    throw Error('There was an internal error while fetching time.');
  };

  addOne = async (single: T): Promise<IResult<T>> => {
    const response = await this._axiosInstance.post<IResult<T>>(this._baseEndpoint, single);
    return APIResponseHandler(response);
  };

  updateOne = async (singleWithChanges: T): Promise<IResult<T>> => {
    const response = await this._axiosInstance.put<IResult<T>>(this._baseEndpoint, singleWithChanges);
    return APIResponseHandler(response);
  };

  // Below is the same as update put the old systems sometimes use patch so I added it.
  patchOne = async (singleWithChanges: T): Promise<IResult<T>> => {
    const response = await this._axiosInstance.patch<IResult<T>>(this._baseEndpoint, singleWithChanges);
    return APIResponseHandler(response);
  };

  deleteOne = async (id: string): Promise<IResult<boolean | undefined>> => {
    const response = await this._axiosInstance.delete<IResult<{}>>(`${this._baseEndpoint}/${id}`);
    return APIResponseHandler(response);
  };
}
